import { fetchCustomerJourneyKey } from "@/api/insuranceApi";
import { fetchTranckingDataFromLegacy, saveContestEntry } from '@/api/legacyAPI';
import { useContest } from '@/composables/useContest';
import { getLicenseYearsOptions, jobOptions, vehicleTypeOptions } from '@/core/formOptions';
import { jsonToFormData, trackGTMEvent } from '@/core/helpers';
import postalCodes from '@/core/postalCodes';
import { FormDataService } from '@/core/services/formDataService';
import { NEWSLETTER_OPTIN_IDENTIFIER } from '@/core/services/optinsService';
import { QuoteService } from '@/core/services/quoteService';
import { useAPIStore } from '@/stores/api';
import { useQuoteStore } from '@/stores/quote';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

export const useFormStore = defineStore('form', () => {
  
  const formData = ref({});
  const router = useRouter();
  const apiStore = useAPIStore();
  const quoteStore = useQuoteStore();
  const quoteService = QuoteService();
  const formDataService = FormDataService()

  const getFormData = async (category)=> {

    let formData = formDataService.get(category)

    if (!formData){
      formData = await formDataService.getLegacyData(quoteStore.fid);
      formData.vehicle = await apiStore.getVehicleByLicensePlate(formData.licensePlate, category)
    }

    return formData
  }

  const resetContestFields = (category, formData) => {
    useContest().reset();

    formDataService.updateEmailValue(category, '');
    formDataService.updateNewsletterOptin(category, false);

    if (formData) {
      formData.email = '';
      formData.newsletter_optin = false;
    }
  }

    const getCustomerJourneyKey = async ()=> {
        return await fetchCustomerJourneyKey();
    }

  const storeFormData = (category, input)=> {
    if (input) {
      formData.value = input;
    }

    formDataService.set(category, input)
  }

  const mapFormData = (formData) => {
    const {
      vehicle,
      usedVehicle,
      licensePlate,
      secondDriver,
      duration,
      packet,
      vehicleType,
      sex,
      job,
      email,
      birthDate,
      postalCode,
      licenseYears,
      birthDate2,
      licenseYears2,
      transferDate
    } = formData;

    return {
      vehicle: vehicle,
      usedVehicle: usedVehicle ?? false,
      licensePlate,
      secondDriver,
      duration: duration ? duration.toString() : '12',
      packet: packet ? packet.toString() : '0',
      vehicleType: typeof vehicleType === 'object' ? vehicleType : findOption(vehicleTypeOptions, vehicleType, "/carinsurance/form"),
      sex: sex ? sex.toString() : "4",
      job: findOption(jobOptions, job?.id, 0),
      birthDate: birthDate,
      email: email ?? '',
      postalCode: typeof postalCode === 'object' ? postalCode : findOption(postalCodes, postalCode, 0),
      licenseYears: findOption(getLicenseYearsOptions(birthDate), licenseYears, "") ?? licenseYears,
      birthDate2: secondDriver && birthDate2 ? birthDate2 : {day: "", month: "", year:""},
      licenseYears2: secondDriver ? findOption(getLicenseYearsOptions(birthDate2), licenseYears2, null) ?? licenseYears2 : null,
      transferDate: transferDate,
    };
  }

    const prepareValidationData = (form, category) => {
        const { contestAccepted } = useContest();

        if (![0, 5, 6, 7, 8, 9, 10, 11, 12, 13].includes(form.vehicleType)) {
            const legacyData = JSON.parse(JSON.stringify(form, replacer));

            delete legacyData.newsletter_optin;
            if (contestAccepted.value) {
                legacyData.optins = { [NEWSLETTER_OPTIN_IDENTIFIER]: form.newsletter_optin === true };
            }
            legacyData.email = contestAccepted.value && form.email ? form.email : '';
            return legacyData;
        }

        // Enable EIQ validation
        // vehicle age
        const now = new Date();
        const currentYear = now.getFullYear();
        let vehicleAge = currentYear - form.vehicle.manufactureYear;
        let firstDriverLicenseIssueDate = new Date(now.setFullYear(currentYear - form.licenseYears));

        // drivers
        let firstDriver = {
            dateOfBirth: `${form.birthDate.year}-${form.birthDate.month}-${form.birthDate.day}T01:01:01+01:00`,
            licenseYears: form.licenseYears,
            licenseIssueDate: generateDate(firstDriverLicenseIssueDate),
        }

        firstDriver.email = contestAccepted.value && form.email ? form.email : '';

        let drivers = [firstDriver]

        if (form.secondDriver) {
            let secondDriverLicenseIssueDate = new Date(now.setFullYear(currentYear - form.licenseYears2));

            let secondDriver = {
                dateOfBirth: `${form.birthDate2.year}-${form.birthDate2.month}-${form.birthDate2.day}T01:01:01+01:00`,
                licenseYears: form.licenseYears2,
                licenseIssueDate: generateDate(secondDriverLicenseIssueDate),
            }

            drivers = [firstDriver, secondDriver]
        }

        // used vehicle - transfer date
        let transferDate = ""
        if (form.usedVehicle) {
            transferDate = `${form.transferDate.year}-${form.transferDate.month}-01T01:01:01+01:00`
        }

        const validationData = {
            vehicle: {
                type: form.vehicleType,
                licensePlate: form.licensePlate,
                manufacturer: form.vehicle.manufacturer,
                manufacturerId: parseInt(form.vehicle.manufacturerId),
                model: form.vehicle.model,
                modelId: parseInt(form.vehicle.modelId),
                edition: form.vehicle.edition,
                editionId: form.vehicle.editionId,
                extraEdition: form.vehicle.vehicleModelExtraEditionText,
                age: vehicleAge,
                manufacturingDate: `${form.vehicle.manufactureYear}-${form.vehicle.manufactureMonth}-01T01:01:01+01:00`,
                isUsed: form.usedVehicle,
                transferDate: transferDate,
                value: form.vehicle.value,
                fuel: form.vehicle.fuel,
                taxableHP: parseInt(form.vehicle.taxableHP),
                combinedHorsepower: form.vehicle.combinedHorsepower,
                cc: form.vehicle.cc,
                kw: form.vehicle.kw,
                acceleration: form.vehicle.acceleration,
                emissions: form.vehicle.emissions,
                hasAlarm: form.vehicle.hasAlarm,
                seats: form.vehicle.seats,
                roofType: form.vehicle.roofType,
                isHeavy: form.vehicle.isHeavy,
                minRecommendedValue: form.vehicle.minRecommendedValue,
                maxRecommendedValue: form.vehicle.maxRecommendedValue
            },
            customer: {
                type: form.customerType,
                postalCode: form.postalCode,
                hasHighTurnover: form.has_high_turnover,
                occupation: form.job,
                drivers: drivers
            },
            contract: {
                duration: form.duration,
                packetType: form.packet,
            },
            context: {
                classification: {
                    type: category,
                },
                user: {
                    offerUser: "",
                    quoteUser: "insurancemarket"
                }
            },
            trackingData: {
                customerJourneyId: quoteService.get().customerJourneyId
            }
        };

        if (contestAccepted.value) {
          validationData.optins = {
            [NEWSLETTER_OPTIN_IDENTIFIER]: form.newsletter_optin === true,
          };
        }

        return validationData;

    }

    function generateDate(date) {
        const year = date.getFullYear();
        const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
        const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);

        return `${year}-${month}-${day}T01:01:01+01:00`;
    }

  // Replacer function to modify the serialization process
  function replacer(key, value) {
    if (typeof value === 'object' && value !== null && 'id' in value && 'label' in value) {
      return value.id;
    }
    // Return the original value
    return value;
  }

  const findOption = (options, value, defaultValue) =>
    options.find(option => option.id == value) || options.find(option => option.id == defaultValue);

  const postFormData = async (values, category, legacyFormValidationPath) => {
    return await apiStore.postFormData(mapToLegacyForm(values, category), legacyFormValidationPath);
  }

  const mapToLegacyForm = (formData, category) => jsonToFormData(prepareValidationData(formData, category))

  const getErrorFields = _ => document.querySelector('.is-invalid').closest('[data-field]');

  const redirectToResults = async (fid, category, email = '', newsletterOptin = false) => {
    const { preBuyID } = await fetchTranckingDataFromLegacy(fid)
    trackGTMEvent({
    	"event": "quote_form_success",
      "pb_id": preBuyID
    });

    const { contestAccepted, contestGtmFired, reset } = useContest()
    if (!contestGtmFired.value) {
      trackGTMEvent({ event: 'october_2026_contest_checkbox', value: false, category });
    }
    if (contestAccepted.value && email) {
      const acceptedNewsletter = newsletterOptin === true;
      await saveContestEntry(email, preBuyID, true, category, acceptedNewsletter).catch(() => {})
      reset()
    }

    return await router.push({
      name: 'results',
      params: { fid, category }
    });
  }

  return {
    formData,
    getFormData,
    resetContestFields,
    getCustomerJourneyKey,
    findOption,
    mapFormData,
    getErrorFields,
    redirectToResults,
    storeFormData,
    postFormData,
  }
})